import { gsap } from "gsap";
import { SplitText } from "../../../plugins/gsap/SplitText";
import HeroBorderDrawer from "./HeroBorderDrawer";
import Sizes from "./Sizes";

gsap.registerPlugin(SplitText);

export class HeroAnimationTimeline {
    constructor(dom) {
        this.html = dom;

        if(this.html.slidePanel.background) {
            this.thesisFrame = new HeroBorderDrawer(this.html.slidePanel.background);
        }

        if(this.html.slidePanel.thesisTextEl) {
            this.thesisText = new SplitText(this.html.slidePanel.thesisTextEl, { type: "lines,words", wordsClass: 'js-word-thesis' });
            this.html.slidePanel.thesisTextHolderEl.style.opacity = 1;
        }

        this.sizes = Sizes.getInstance();
        this.sizes.on('resize', this.resize);
    }

    createTimeline(tl, index, isForward = true) {
        console.log('Sequential animation to', index, 'isForward', isForward);
        if (isForward) {
            switch(index) {
                case 0:
                    this.timelineForwardAppearHero(tl)
                    break;
                case 1:
                    this.timelineForwardRetractHero(tl)
                    this.timelineForwardAppearCompendium(tl)
                    break;
                case 2:
                    this.timelineForwardAppearCompendiumCards(tl)
                    break;
                case 3:
                    this.timelineForwardRetractCompendiumCards(tl)
                    this.timelineForwardAppearLatest(tl)
                    break;
                case 4:
                    this.timelineForwardAppearEvolution(tl)
                    break;
            }
        } else {
            // Reverse animations
            switch(index) {
                case 3:
                    // reverse step 5
                    this.timelineReverseRetractEvolution(tl);
                    break;
                case 2:
                    this.timelineReverseRetractLatest(tl);
                    this.timelineReverseAppearCompendiumCards(tl);
                    break;
                case 1: // Going back to section 1 from section 2
                    this.timelineReverseRetractCompendiumCards(tl)
                    break;

                case 0: // Retracting section 1 slide panel
                    this.timelineReverseRetractCompendium(tl)
                    this.timelineReverseAppearHero(tl)
                    break;
            }
        }
    }

    timelineReverseRetractLatest(tl) {
        tl
            .to(this.html.latest.slider, {
                y: 50,
                autoAlpha: 0,
                duration: 1,
                ease: 'power2.out'
            })
            .to(this.html.latest.heading, {
                y: 50,
                autoAlpha: 0,
                duration: 0.6,
                stagger: {
                    amount: 0.1,
                    from: 'end'
                },
                ease: 'power2.out'
            }, 0.1)
            .set(this.html.latest.el, {
                opacity: 0,
                visibility: 'hidden'
            })
    }

    timelineReverseAppearCompendiumCards(tl) {
        tl
            .addLabel('latest')
            .to(this.html.landing.cards, {
                y: 0,
                autoAlpha: 1,
                duration: 1,
                stagger: {
                    amount: 0.2
                },
                ease: 'power2.out'
            }, 'latest')
            .to(this.html.landing.heading, {
                y: 0,
                autoAlpha: 1,
                duration: 0.6,
                stagger: 0.1,
                ease: 'power2.out'
            }, 'latest')
            .set(this.html.latest.el, {
                opacity: 0,
                visibility: 'hidden'
            })
    }

    timelineReverseRetractCompendiumCards(tl) {
        tl
            .to(this.html.landing.cards, {
                y: 50,
                autoAlpha: 0,
                duration: 1,
                stagger: {
                    amount: 0.2
                },
                ease: 'power2.out'
            })
            .to(this.html.landing.header, {
                y: ((this.html.landing.el.clientHeight - this.html.landing.header.clientHeight) / 2) - parseInt(window.getComputedStyle(this.html.landing.header).marginBottom) - 50,
                duration: 1,
                ease: 'power2.out'
            }, 0.6)
    }

    timelineReverseRetractCompendium(tl) {
        tl
        .to(this.html.landing.verticalLines, {
            scaleY: 0,
            duration: 0.6,
            force3D: true,
            ease: 'power2.out'
        })
        .add(() => {
            this.html.subnav.el?.classList.remove('active')
        }, 0)
        .to(this.html.landing.grid, {
            autoAlpha: 0,
            duration: 0.6,
            ease: 'power2.out'
        }, 0)
        .to(this.html.landing.heading, {
            y: 50,
            autoAlpha: 0,
            duration: 0.5,
            stagger: {
                amount: 0.1,
                from: 'end'
            },
            ease: 'power2.out'
        }, 0)
        .set(this.html.landing.el, {
            opacity: 0,
            visibility: 'hidden'
        })
    }

    timelineReverseAppearHero(tl) {
        tl
            .addLabel('landing')
            .to(this.html.slidePanel.el, {
                x: window.innerWidth - this.html.slidePanel.el.offsetWidth - (window.innerWidth >= 1200 ? 110 : 50),
                duration: 1,
                force3D: true,
                ease: "power3.out"
            }, 'landing')
            .to(this.html.slidePanel.thesisTextHolderEl, {
                opacity: 1,
                duration: 1,
                ease: "power2.out",
            }, 'landing')
            .add(() => {
                this.html.landing.animatedGridEl.classList.remove('visible')
            })
    }

    timelineForwardAppearHero(tl) {
        // Constants for animation timing and offsets
        const ANIMATION_DURATION = 1;
        const SLIDE_PANEL_DELAY = 1.5;
        const THESIS_DELAY = 1.7;
        const OFFSET = window.innerWidth >= 1200 ? 110 : 50;

        const progressList = {
            thesisFrame: 0
        };

        // Common animation properties
        const commonProps = {
            duration: ANIMATION_DURATION,
            ease: 'power2.out'
        };

        // Initial hero animations
        tl.to(this.html.hero.logo, {
            ...commonProps,
            force3D: true,
            y: 0,
            opacity: 1
        })
        .to(this.html.hero.title, {
            ...commonProps,
            force3D: true,
            y: 0,
            opacity: 1,
            onComplete: () => {
                window.dispatchEvent(new CustomEvent('introTitleComplete'));
            }
        }, '-=0.5')
        .to(this.html.hero.heading, {
            ...commonProps,
            force3D: true,
            y: 0,
            opacity: 1
        }, '-=0.5')

        // Slide panel and thesis animations
        .fromTo(this.html.slidePanel.el,
            {
                x: window.innerWidth - 110
            },
            {
                ...commonProps,
                force3D: true,
                x: window.innerWidth - this.html.slidePanel.el.offsetWidth - OFFSET
            },
            SLIDE_PANEL_DELAY
        )
        .to(progressList, {
            ...commonProps,
            thesisFrame: 100,
            onUpdate: () => {
                this.thesisFrame?.update(progressList.thesisFrame);
            }
        }, THESIS_DELAY)
        .to(this.thesisText.words, {
            duration: 0.4,
            force3D: true,
            ease: 'power2.out',
            opacity: 1,
            stagger: {
                amount: 0.6
            }
        }, THESIS_DELAY);
    }

    // Fast/instant version of hero intro for anchor navigation on page load
    timelineForwardAppearHeroInstant(tl) {
        console.log('Using instant hero intro for anchor navigation');
        const OFFSET = window.innerWidth >= 1200 ? 110 : 50;
        
        // Set all hero elements to their final states immediately
        tl.set(this.html.hero.logo, {
            y: 0,
            opacity: 1
        })
        .set(this.html.hero.title, {
            y: 0,
            opacity: 1,
            onComplete: () => {
                window.dispatchEvent(new CustomEvent('introTitleComplete'));
            }
        })
        .set(this.html.hero.heading, {
            y: 0,
            opacity: 1
        })
        .set(this.html.slidePanel.el, {
            x: window.innerWidth - this.html.slidePanel.el.offsetWidth - OFFSET
        });
        
        // Update thesis frame to completed state
        if (this.thesisFrame) {
            this.thesisFrame.update(100);
        }
        
        // Set thesis text to visible
        if (this.thesisText && this.thesisText.words) {
            tl.set(this.thesisText.words, {
                opacity: 1
            });
        }
        
        // Add a small delay to allow the DOM to update
        tl.to({}, {duration: 0.1});
        
        return tl;
    }

    timelineForwardRetractHero(tl) {
        // Add will-change: transform before animation for better performance
        gsap.set(this.html.slidePanel.el, {
            willChange: 'transform'
        });

        tl
        .to(this.html.slidePanel.el, {
            x: - this.html.slidePanel.el.offsetWidth,
            duration: 1,
            ease: "power2.out",
            onComplete: () => {
                // Remove will-change after animation completes
                gsap.set(this.html.slidePanel.el, {
                    willChange: 'auto'
                });
            }
        }, 0)
        .to(this.html.slidePanel.thesisTextHolderEl, {
            opacity: 0,
            duration: 1,
            ease: "power2.out",
        }, 0)
    }

    timelineForwardAppearCompendiumInstant(tl) {
        tl
        .set(this.html.landing.el, {
            opacity: 1,
            visibility: 'visible'
        })
        .set(this.html.landing.heading, {
            y: 0,
            autoAlpha: 1,
        })
        .set(this.html.landing.grid, {
            autoAlpha: 1,
        })
        .set(this.html.landing.verticalLines, {
            scaleY: 1,
        })
        .add(() => {
            this.html.landing.animatedGridEl.classList.add('visible')
        })
        .add(() => {
            this.html.subnav.el?.classList.add('active')
        })
    }

    timelineForwardAppearCompendium(tl) {
        tl
        .set(this.html.landing.el, {
            opacity: 1,
            visibility: 'visible'
        })
        .addLabel('landing')
        .to(this.html.landing.heading, {
            y: 0,
            autoAlpha: 1,
            duration: 0.5,
            stagger: 0.1,
            ease: 'power2.out'
        }, 'landing')
        .to(this.html.landing.grid, {
            autoAlpha: 1,
            duration: 0.6,
            ease: 'power2.out'
        }, 'landing')
        .to(this.html.landing.verticalLines, {
            scaleY: 1,
            duration: 0.6,
            force3D: true,
            ease: 'power2.out'
        }, 'landing')
        .add(() => {
            this.html.landing.animatedGridEl.classList.add('visible')
        }, 'landing')
        .add(() => {
            this.html.subnav.el?.classList.add('active')
        }, 'landing')

    }

    timelineForwardAppearCompendiumCards(tl) {
        tl
            .to(this.html.landing.header, {
                y: 0,
                duration: 1,
                ease: 'power2.out'
            })
            .to(this.html.landing.cards, {
                y: 0,
                autoAlpha: 1,
                duration: 1,
                stagger: {
                    amount: 1
                },
                ease: 'power2.out'
            }, 0.6)
    }

    timelineForwardRetractCompendiumCards(tl) {
        tl
            .to(this.html.landing.heading, {
                y: -50,
                autoAlpha: 0,
                duration: 0.5,
                stagger: 0.1,
                ease: 'power2.out'
            })
            .to(this.html.landing.cards, {
                y: -50,
                autoAlpha: 0,
                duration: 1,
                stagger: {
                    amount: 0.2
                },
                ease: 'power2.out'
            }, 0.2)
    }

    timelineForwardAppearLatest(tl) {
        tl
            .set(this.html.latest.el, {
                opacity: 1,
                visibility: 'visible'
            })
            .addLabel('latest')
            .to(this.html.latest.heading, {
                y: 0,
                autoAlpha: 1,
                duration: 0.8,
                stagger: {
                    amount: 0.1
                },
                ease: 'power2.out'
            }, 'latest')
            .to(this.html.latest.slider, {
                y: 0,
                autoAlpha: 1,
                duration: 1,
                ease: 'power2.out'
            }, 'latest')
    }

    timelineForwardAppearEvolution(tl) {
        tl
            .set(this.html.evolution.el, {
                opacity: 1,
                visibility: 'visible'
            })
            .to(this.html.evolution.background, {
                autoAlpha: 1,
                duration: 1,
                ease: 'power2.out'
            })
            .to(this.html.evolution.heading, {
                y: 0,
                autoAlpha: 1,
                duration: 0.8,
                stagger: {
                    amount: 0.1,
                },
                ease: 'power2.out'
            })
            .to(this.html.evolution.videos, {
                y: 0,
                autoAlpha: 1,
                duration: 1,
                force3D: true,
                ease: 'power2.out'
            }, "-=0.7")
            .add(() => {
                this.html.landing.animatedGridEl.classList.remove('visible')
            })
    }

    timelineReverseRetractEvolution(tl) {
        tl
        .to(this.html.evolution.videos, {
            y: 50,
            autoAlpha: 0,
            duration: 1,
            ease: 'power2.out'
        })
        .to(this.html.evolution.heading, {
            y: 50,
            autoAlpha: 0,
            duration: 0.6,
            stagger: {
                amount: 0.1,
                from: 'end'
            },
            ease: 'power2.out'
        }, 0.2)
        .to(this.html.evolution.background, {
            autoAlpha: 0,
            duration: .4,
            ease: 'none'
        }, "-=0.1")
        .set(this.html.evolution.el, {
            opacity: 0,
            visibility: 'hidden'
        })
        .add(() => {
            this.html.landing.animatedGridEl.classList.add('visible')
        })
    }

    timelineReverseRetractEvolutionInstant(tl) {
        tl
        .set(this.html.evolution.videos, {
            y: 50,
            autoAlpha: 0,
        })
        .set(this.html.evolution.heading, {
            y: 50,
            autoAlpha: 0,
        })
        .set(this.html.evolution.background, {
            autoAlpha: 0,
        })
        .set(this.html.evolution.el, {
            opacity: 0,
            visibility: 'hidden'
        })
        .add(() => {
            this.html.landing.animatedGridEl.classList.add('visible')
        })
    }

    createNonSequentialTimeline(tl, fromIndex, toIndex) {
        console.log('Non-sequential animation from', fromIndex, 'to', toIndex);

        // Handle transitions to regular anchors (indices 5, 6)
        if (toIndex >= 5 && fromIndex <= 4) {
            // We need to animate to the last panel first (index 4)
            this.animateToLastPanel(tl, fromIndex);
            return tl;
        }

        if(fromIndex > 4 && toIndex <= 4) {
            console.log('Evolution to', toIndex);
            switch(toIndex) {
                case 3:
                    this.timelineReverseRetractEvolutionInstant(tl);
                    break;
                case 2:
                    this.timelineReverseRetractEvolutionInstant(tl);
                    this.timelineEvolutionToCompendiumCardsInstant(tl);
                    this.timelineReverseAppearCompendiumCards(tl);
                    break;
                default:
                    break;
            }

            return tl;
        }

        // Handle specific transition pairs
        const transition = `${fromIndex}->${toIndex}`;
        switch (transition) {    
            case '1->3': // Compendium to Latest
                this.timelineForwardRetractCompendiumCards(tl);
                this.timelineCompendiumToLatestInstant(tl);
                this.timelineForwardAppearLatest(tl);
                break;

            case '1->4': // Compendium to Evolution
                this.timelineForwardRetractCompendiumCards(tl);
                this.timelineForwardAppearEvolution(tl);
                this.timelineIntermediateCompendiumToEvolution(tl);
                break;

            case '2->4': // Compendium with cards to Evolution
                this.timelineForwardRetractCompendiumCards(tl);
                this.timelineForwardAppearEvolution(tl);
                this.timelineLatestToEvolutionInstant(tl);
                break;

            case '4->2': // Evolution to Compendium with cards
                this.timelineEvolutionToCompendiumCardsInstant(tl)
                this.timelineReverseRetractEvolution(tl)
                this.timelineReverseAppearCompendiumCards(tl)
                break;
            default:
                console.warn('Unhandled non-sequential transition:', transition);
                break;
        }

        return tl;
    }

    initialAnimateToLastPanel(tl, fromIndex) {
        switch(fromIndex) {
            case 0:
                // From Hero to Evolution
                this.timelineForwardRetractHero(tl);
                this.timelineForwardAppearCompendiumInstant(tl);
                this.timelineForwardRetractCompendiumCards(tl);
                break;
            case 1:
                // From Compendium to Evolution
                this.timelineForwardRetractCompendiumCards(tl);
                break;
            case 2:
                // From Compendium with cards to Evolution
                this.timelineForwardRetractCompendiumCards(tl);
                break;
            case 3:
                // No animation needed
                break;
            case 4:
                // Already at Evolution, no animation needed
                break;
        }

        return tl;
    }

    // Helper method to animate to the last panel (for regular anchor transitions)
    animateToLastPanel(tl, fromIndex) {
        switch(fromIndex) {
            case 0:
                this.timelineIntermediateCompendiumToLastPanel(tl);
                break;
            case 1:
                // From Compendium to Evolution
                this.timelineIntermediateCompendiumToLastPanel(tl);
                break;
            case 2:
                // From Compendium with cards to Evolution
                this.timelineIntermediateCompendiumToLastPanel(tl);
                break;
            case 3:
                // From Latest to Evolution
                this.timelineIntermediateCompendiumToLastPanel(tl);
                break;
            case 4:
                // Already at Evolution, no animation needed
                break;
        }

        return tl;
    }

    timelineCompendiumToLatestInstant(tl) {
        tl
        .set(this.html.landing.header, {
            y: 0
        })
        .set(this.html.landing.heading, {
            y: -50,
            autoAlpha: 0
        })
        .set(this.html.landing.cards, {
            y: -50,
            autoAlpha: 0
        })
    }

    timelineLatestToEvolutionInstant(tl) {
        tl
        .set(this.html.latest.el, {
            opacity: 1,
            visibility: 'visible'
        })
        .set(this.html.latest.heading, {
            y: 0,
            autoAlpha: 1
        })
        .set(this.html.latest.slider, {
            y: 0,
            autoAlpha: 1
        })
    }

    timelineShowEvolutionInstant(tl) {
        tl
        .set(this.html.evolution.el, {
            opacity: 1,
            visibility: 'visible'
        })
        .set(this.html.evolution.background, {
            autoAlpha: 1
        })
        .set(this.html.evolution.heading, {
            y: 0,
            autoAlpha: 1
        })
        .set(this.html.evolution.videos, {
            y: 0,
            autoAlpha: 1,
        })
        .add(() => {
            this.html.landing.animatedGridEl.classList.remove('visible')
        })
    }

    timelineIntermediateCompendiumToEvolution(tl) {
        this.timelineCompendiumToLatestInstant(tl);
        this.timelineLatestToEvolutionInstant(tl);
    }

    timelineIntermediateCompendiumToLastPanel(tl) {
        this.timelineCompendiumToLatestInstant(tl);
        this.timelineLatestToEvolutionInstant(tl);
        this.timelineShowEvolutionInstant(tl);
    }

    timelineEvolutionToCompendiumCardsInstant(tl) {
        tl
            .set(this.html.latest.slider, {
                y: 50,
                autoAlpha: 0,
            })
            .set(this.html.latest.heading, {
                y: 50,
                autoAlpha: 0,
            })
            .set(this.html.latest.el, {
                opacity: 0,
                visibility: 'hidden'
            })
    }

    resize = () => {
        console.log('resize');

        // Update slide panel position if it exists
        if (this.html.slidePanel?.el) {
            const OFFSET = window.innerWidth >= 1200 ? 110 : 50;
            // Adjust position based on current animation state
            if (this.html.slidePanel.el.style.transform.includes('-')) {
                // Panel is hidden to the left
                gsap.set(this.html.slidePanel.el, {
                    x: -this.html.slidePanel.el.offsetWidth
                });
            } else {
                // Panel is visible
                gsap.set(this.html.slidePanel.el, {
                    x: window.innerWidth - this.html.slidePanel.el.offsetWidth - OFFSET
                });
            }
        }

        // Recalculate header position if needed
        if (this.html.landing?.header) {
            // Reset header position if it's in middle state
            if (parseFloat(gsap.getProperty(this.html.landing.header, "y")) > 0) {
                const centerY = ((this.html.landing.el.clientHeight - this.html.landing.header.clientHeight) / 2)
                    - parseInt(window.getComputedStyle(this.html.landing.header).marginBottom) - 50;
                gsap.set(this.html.landing.header, {
                    y: centerY
                });
            }
        }

        // Redraw thesis frame if it exists
        if (this.thesisFrame) {
            this.thesisFrame.resize();
        }

        // Re-split thesis text if it exists
        if (this.thesisText && this.html.slidePanel.thesisTextEl) {
            // Revert changes from previous SplitText
            this.thesisText.revert();
            // Create new split text with updated dimensions
            this.thesisText = new SplitText(this.html.slidePanel.thesisTextEl, {
                type: "lines,words",
                wordsClass: 'js-word-thesis'
            });

            // Ensure proper visibility
            this.html.slidePanel.thesisTextHolderEl.style.opacity = 1;

            // Re-apply animations if thesis is currently visible
            if (parseFloat(window.getComputedStyle(this.html.slidePanel.thesisTextHolderEl).opacity) > 0) {
                gsap.set(this.thesisText.words, { opacity: 1 });
            }
        }
    }

}

export default HeroAnimationTimeline;
